import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { TagWebDto } from '@/features/dashboard/tags/types/TagWebDto.ts';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import TagChip from '@/features/dashboard/tags/components/TagChip.tsx';
import TagPickerPopover from '@/features/dashboard/tags/components/TagPickerPopover.tsx';
import AddTagAffordance from '@/features/dashboard/tags/components/AddTagAffordance.tsx';
import { CellChipList } from '@/components/table/_fieldDefinitions/_base/CellChipList.tsx';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';

interface TagsCellProps {
  tags: TagWebDto[];
  /** When present (with targetId), the cell becomes editable via a popover. */
  targetType?: TaggableType;
  targetId?: string;
  /** Table data query key — invalidated after inline edits to refresh rows. */
  queryKey?: QueryKey;
}

const TagsCell = ({ tags, targetType, targetId, queryKey }: TagsCellProps) => {
  const queryClient = useQueryClient();
  const editable = !!targetType && !!targetId;

  if (tags.length === 0 && !editable) {
    return <EmptyValue />;
  }

  const content =
    tags.length === 0 ? (
      <AddTagAffordance />
    ) : (
      <CellChipList
        items={tags.map((tag) => ({
          key: tag.id ?? tag.name,
          label: tag.name,
          chip: <TagChip tag={tag} className="shrink-0" />,
        }))}
      />
    );

  if (!editable) {
    return content;
  }

  return (
    <TagPickerPopover
      targetType={targetType}
      targetId={targetId}
      onMutated={() => {
        if (queryKey) {
          queryClient.invalidateQueries({ queryKey });
        }
      }}
    >
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        aria-label="Edytuj tagi"
        // Wiersz ma na hoverze dokładnie `neutral-50`, a najechanie na komórkę
        // zawsze oznacza najechanie na wiersz — podświetlenie musi być o stopień
        // ciemniejsze, żeby w ogóle było widoczne.
        className="flex w-full items-center gap-1 overflow-hidden rounded-sm py-0.5 text-left hover:bg-neutral-100"
      >
        {content}
      </button>
    </TagPickerPopover>
  );
};

export default TagsCell;
